import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useQuery } from "react-query";
import { apiService } from "../services/api";

interface TypeaheadPrefixInputProps {
  onSelect: (value: string) => void;
  excludeValues?: string[];
  placeholder?: string;
}

export default function TypeaheadPrefixInput({
  onSelect,
  excludeValues = [],
  placeholder = "Search prefixes...",
}: TypeaheadPrefixInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: prefixesData } = useQuery(
    "dt-api-key-prefixes",
    () => apiService.getDtAPIKeyPrefixes(),
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 },
  );

  const allOptions = ((prefixesData?.data?.data as string[]) || [])
    .filter((p) => !excludeValues.includes(p))
    .map((p) => ({ label: p, value: p }));

  const filtered = inputValue.trim()
    ? allOptions.filter((opt) =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : allOptions;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    onSelect(value);
    setInputValue("");
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
            if (e.key === "Enter" && filtered.length === 1) {
              e.preventDefault();
              handleSelect(filtered[0]!.value);
            }
          }}
          placeholder={placeholder}
          className="input w-full pr-8"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {isOpen && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          {filtered.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt.value)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
